import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { MachineContext } from '../DataContexts/MachineContext';
import { formatRelative, isCashAlert, isDaysAlert, isOnline } from './pinCard';
import { CLOCK_ICON_SVG, EURO_ICON_SVG } from './markerStyle';
import { formatPlanningAddress, planningId } from './planning';
import './PlanningPanel.css';

// DJB-248: the "Planning" notepad next to the Accounts › Maps map (right of
// the map on desktop, under it on mobile — AccountMaps.css). The operator
// collects the locations of the day here from the pin cards ("Add to
// planning", pinCard.js). Per entry: name + the € / clock trigger glyphs of
// the Detail pins (markerStyle.js, same cash > €150 / days > 90 rules as the
// card), and the address with the online / offline dot in front of it.
// Entries are separated by a thin divider, a dotted "select or drag more
// locations" line closes the list and "Clear all (n)" sits bottom right.
//
// DJB-249: the 6-dots handle on the right drags an entry to a new place in
// the list (native drag & drop; ↑ / ↓ on the focused handle does the same
// for keyboards). Hovering, focusing or tapping an entry reveals "Show in
// Maps" (pans the map to the pin and opens its card) and × (remove).
// Persistence lives in usePlanning.js / planning.js — this component only
// renders the resolved entries it is handed.

export const PLANNING_TITLE = 'Planning';
export const PLANNING_SUBTITLE = 'Select a location to add to planning.';
export const PLANNING_MORE = 'select or drag more locations';
export const SHOW_IN_MAPS_LABEL = 'Show in Maps';
export const clearAllLabel = (count) => `Clear all (${count})`;

const DRAG_MIME = 'application/x-fe-jukebox-planning';

// 2 × 3 dots, drawn as circles so they stay crisp and take the text colour
const HandleGlyph = () => (
  <svg className="planning-entry-handle-glyph" viewBox="0 0 10 16" aria-hidden="true" focusable="false">
    <circle cx="3" cy="3" r="1.4" />
    <circle cx="7" cy="3" r="1.4" />
    <circle cx="3" cy="8" r="1.4" />
    <circle cx="7" cy="8" r="1.4" />
    <circle cx="3" cy="13" r="1.4" />
    <circle cx="7" cy="13" r="1.4" />
  </svg>
);

// the DJB-243 glyphs are inline-SVG strings (shared with the Leaflet divIcon)
const TriggerGlyph = ({ kind, title }) => (
  <span
    className={`planning-entry-trigger is-${kind}`}
    role="img"
    aria-label={title}
    title={title}
    dangerouslySetInnerHTML={{ __html: kind === 'cash' ? EURO_ICON_SVG : CLOCK_ICON_SVG }}
  />
);

const PlanningEntry = ({
  entry,
  address,
  now,
  selected,
  dragging,
  dropEdge,
  onSelect,
  onShowInMaps,
  onRemove,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onHandleTouchStart,
  onHandleKeyDown,
}) => {
  const [armed, setArmed] = useState(false);
  const id = planningId(entry.machineId);
  const online = isOnline(entry.lastSeen, now);
  const cash = isCashAlert(entry.cash);
  const days = isDaysAlert(entry.daysSinceCashout);
  const name = entry.name || 'Naamloze machine';
  const seenLabel = `${online ? 'Online' : 'Offline'} · laatst gezien ${formatRelative(entry.lastSeen, now)}`;

  const className = [
    'planning-entry',
    selected && 'is-selected',
    dragging && 'is-dragging',
    dropEdge === 'before' && 'is-drop-before',
    dropEdge === 'after' && 'is-drop-after',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <li
      className={className}
      data-machine-id={id}
      data-selected={selected ? 'true' : 'false'}
      // only the handle starts a drag: the li is draggable while the handle is held
      draggable={armed}
      onDragStart={(event) => {
        if (!armed) {
          event.preventDefault();
          return;
        }
        if (event.dataTransfer) {
          try {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData(DRAG_MIME, id);
            event.dataTransfer.setData('text/plain', name);
          } catch (error) {
            // some browsers refuse custom types; the id travels in state anyway
          }
        }
        onDragStart(id);
      }}
      onDragOver={(event) => onDragOver(event, id)}
      onDragLeave={() => onDragLeave(id)}
      onDrop={(event) => onDrop(event, id)}
      onDragEnd={() => {
        setArmed(false);
        onDragEnd();
      }}
      onClick={(event) => {
        if (event.target.closest('button')) return;
        onSelect(id);
      }}
    >
      <div className="planning-entry-main">
        <div className="planning-entry-line planning-entry-line-name">
          <span className="planning-entry-name" title={name}>
            {name}
          </span>
          {cash && <TriggerGlyph kind="cash" title="cash > €150" />}
          {days && <TriggerGlyph kind="days" title="days > 90" />}
        </div>
        <div className="planning-entry-line planning-entry-line-address">
          <span
            className={`planning-entry-status ${online ? 'is-online' : 'is-offline'}`}
            role="img"
            aria-label={seenLabel}
            title={seenLabel}
          />
          <span className="planning-entry-address" title={address}>
            {address}
          </span>
        </div>
      </div>
      <div className="planning-entry-actions">
        <button
          type="button"
          className="planning-entry-show"
          onClick={() => onShowInMaps(entry)}
          title={`${SHOW_IN_MAPS_LABEL}: ${name}`}
        >
          {SHOW_IN_MAPS_LABEL}
        </button>
        <button
          type="button"
          className="planning-entry-remove"
          onClick={() => onRemove(id)}
          aria-label={`Remove ${name} from planning`}
          title="Remove from planning"
        >
          <FaTimes aria-hidden="true" />
        </button>
      </div>
      <button
        type="button"
        className="planning-entry-handle"
        aria-label={`Drag to reorder ${name}`}
        title="Drag to reorder (↑ / ↓ with the keyboard)"
        onMouseDown={() => setArmed(true)}
        onMouseUp={() => setArmed(false)}
        onBlur={() => setArmed(false)}
        // touch: no native drag & drop on Android, so the panel tracks the
        // finger itself (touch-action: none on the handle keeps the page still)
        onTouchStart={(event) => onHandleTouchStart(event, id)}
        onKeyDown={(event) => onHandleKeyDown(event, id)}
      >
        <HandleGlyph />
      </button>
    </li>
  );
};

const PlanningPanel = ({
  entries = [],
  now,
  onShowInMaps,
  onRemove,
  onMove,
  onMoveTo,
  onClear,
}) => {
  const { machines: fleet = [] } = useContext(MachineContext) || {};
  const [selectedId, setSelectedId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [drop, setDrop] = useState(null); // { id, edge: 'before' | 'after' }
  const dragIdRef = useRef(null);
  const renderNow = now || new Date();

  const fleetById = useMemo(() => {
    const map = new Map();
    (fleet || []).forEach((machine) => {
      const id = planningId(machine && machine.id);
      if (id) map.set(id, machine);
    });
    return map;
  }, [fleet]);

  const ids = entries.map((entry) => planningId(entry.machineId));
  const count = entries.length;

  const toggleSelect = (id) => setSelectedId((current) => (current === id ? null : id));

  const startDrag = (id) => {
    dragIdRef.current = id;
    setDraggingId(id);
  };

  const endDrag = () => {
    dragIdRef.current = null;
    setDraggingId(null);
    setDrop(null);
  };

  const dropEdgeFor = (event, targetId) => {
    const target = event.currentTarget;
    const rect = target && target.getBoundingClientRect ? target.getBoundingClientRect() : null;
    if (!rect || !rect.height) {
      // no layout (jsdom) → keep list order semantics: dropping on a later
      // entry lands after it, on an earlier one before it
      const from = ids.indexOf(dragIdRef.current);
      const to = ids.indexOf(targetId);
      return to > from ? 'after' : 'before';
    }
    return event.clientY - rect.top > rect.height / 2 ? 'after' : 'before';
  };

  const dragOver = (event, targetId) => {
    if (!dragIdRef.current) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    if (targetId === dragIdRef.current) {
      setDrop(null);
      return;
    }
    const edge = dropEdgeFor(event, targetId);
    setDrop((current) =>
      current && current.id === targetId && current.edge === edge ? current : { id: targetId, edge }
    );
  };

  const dragLeave = (targetId) => {
    setDrop((current) => (current && current.id === targetId ? null : current));
  };

  const dropOn = (event, targetId) => {
    const sourceId = dragIdRef.current;
    if (!sourceId) return;
    event.preventDefault();
    if (targetId !== sourceId && onMoveTo) {
      const edge = dropEdgeFor(event, targetId);
      onMoveTo(sourceId, targetId, { after: edge === 'after' });
    }
    endDrag();
  };

  // Touch reorder: the entry under the finger (elementFromPoint) is the drop
  // target, top / bottom half decides before / after, lifting the finger
  // drops. Listeners live on the document for the duration of one gesture.
  const touchRef = useRef(null); // { id, drop: { id, edge } | null }
  const touchStart = (event, id) => {
    if (touchRef.current || !onMoveTo) return;
    const touch = event.touches && event.touches[0];
    if (!touch) return;
    const state = { id, drop: null };
    touchRef.current = state;
    dragIdRef.current = id;
    setDraggingId(id);

    const onMove = (moveEvent) => {
      const point = moveEvent.touches && moveEvent.touches[0];
      if (!point || typeof document.elementFromPoint !== 'function') return;
      const under = document.elementFromPoint(point.clientX, point.clientY);
      const row = under && under.closest ? under.closest('.planning-entry') : null;
      const targetId = row ? row.dataset.machineId : null;
      if (!targetId || targetId === id) {
        state.drop = null;
        setDrop(null);
        return;
      }
      const rect = row.getBoundingClientRect();
      let edge;
      if (rect.height) {
        edge = point.clientY - rect.top > rect.height / 2 ? 'after' : 'before';
      } else {
        edge = ids.indexOf(targetId) > ids.indexOf(id) ? 'after' : 'before';
      }
      state.drop = { id: targetId, edge };
      setDrop((current) =>
        current && current.id === targetId && current.edge === edge ? current : state.drop
      );
    };
    const finish = (commit) => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onCancel);
      if (commit && state.drop) {
        onMoveTo(id, state.drop.id, { after: state.drop.edge === 'after' });
      }
      touchRef.current = null;
      endDrag();
    };
    const onEnd = () => finish(true);
    const onCancel = () => finish(false);
    state.cancel = onCancel;
    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('touchend', onEnd);
    document.addEventListener('touchcancel', onCancel);
  };

  // unmount mid-gesture → drop the document listeners
  useEffect(
    () => () => {
      if (touchRef.current && touchRef.current.cancel) touchRef.current.cancel();
    },
    []
  );

  const handleKeyDown = (event, id) => {
    const index = ids.indexOf(id);
    if (index < 0 || !onMove) return;
    if (event.key === 'ArrowUp' && index > 0) {
      event.preventDefault();
      onMove(index, index - 1);
    } else if (event.key === 'ArrowDown' && index < ids.length - 1) {
      event.preventDefault();
      onMove(index, index + 1);
    }
  };

  const remove = (id) => {
    if (selectedId === id) setSelectedId(null);
    if (onRemove) onRemove(id);
  };

  return (
    <aside className="planning-panel" aria-labelledby="planning-panel-title" data-count={count}>
      <header className="planning-panel-head">
        <h2 id="planning-panel-title" className="planning-panel-title">
          {PLANNING_TITLE}
        </h2>
        <p className="planning-panel-subtitle">{PLANNING_SUBTITLE}</p>
      </header>

      <div className="planning-panel-body">
        {count > 0 && (
          <ol className="planning-list" aria-label={PLANNING_TITLE}>
            {entries.map((entry) => {
              const id = planningId(entry.machineId);
              return (
                <PlanningEntry
                  key={id}
                  entry={entry}
                  address={formatPlanningAddress(entry, fleetById.get(id))}
                  now={renderNow}
                  selected={selectedId === id}
                  dragging={draggingId === id}
                  dropEdge={drop && drop.id === id ? drop.edge : null}
                  onSelect={toggleSelect}
                  onShowInMaps={(machine) => onShowInMaps && onShowInMaps(machine)}
                  onRemove={remove}
                  onDragStart={startDrag}
                  onDragOver={dragOver}
                  onDragLeave={dragLeave}
                  onDrop={dropOn}
                  onDragEnd={endDrag}
                  onHandleTouchStart={touchStart}
                  onHandleKeyDown={handleKeyDown}
                />
              );
            })}
          </ol>
        )}
        <p className={`planning-panel-more${count === 0 ? ' is-empty' : ''}`}>{PLANNING_MORE}</p>
      </div>

      {count > 0 && (
        <footer className="planning-panel-foot">
          <button type="button" className="planning-panel-clear" onClick={() => onClear && onClear()}>
            {clearAllLabel(count)}
          </button>
        </footer>
      )}
    </aside>
  );
};

export default PlanningPanel;
